export type WinnerCountMetricKey =
  | 'fitness'
  | 'energy'
  | 'cost'
  | 'responseTime'
  | 'executionTime';

export type WinnerCountSummaryBasis = 'mean' | 'median';

export interface WinnerCountRankingRow {
  algorithmKey: string;
  algorithmName: string;
  value: number;
}

export interface WinnerCountRow {
  algorithmKey: string;
  algorithmName: string;
  winCount: number;
}

export interface WinnerCountCondition<TMetricKey extends string = WinnerCountMetricKey> {
  rankingsByMean: Record<TMetricKey, WinnerCountRankingRow[]>;
  rankingsByMedian: Record<TMetricKey, WinnerCountRankingRow[]>;
}

export interface WinnerCountSummary<TMetricKey extends string = WinnerCountMetricKey> {
  byMetric: Record<TMetricKey, WinnerCountRow[]>;
  overall: WinnerCountRow[];
}

export interface WinnerCountLeaderGroup {
  leaders: WinnerCountRankingRow[];
  value: number | null;
  isTie: boolean;
  nextDistinctValue: number | null;
  marginToNextDistinct: number | null;
}

export function getTopRankedLeaderGroup<TMetricKey extends string>(
  condition: WinnerCountCondition<TMetricKey>,
  metricKey: TMetricKey,
  basis: WinnerCountSummaryBasis
): WinnerCountLeaderGroup {
  const ranking =
    basis === 'mean'
      ? condition.rankingsByMean[metricKey]
      : condition.rankingsByMedian[metricKey];

  if (!ranking || ranking.length === 0) {
    return {
      leaders: [],
      value: null,
      isTie: false,
      nextDistinctValue: null,
      marginToNextDistinct: null,
    };
  }

  const winningValue = ranking[0].value;
  const leaders = ranking.filter((row) => row.value === winningValue);
  const nextDistinct = ranking.find((row) => row.value !== winningValue) ?? null;

  return {
    leaders,
    value: winningValue,
    isTie: leaders.length > 1,
    nextDistinctValue: nextDistinct?.value ?? null,
    marginToNextDistinct:
      nextDistinct !== null ? Math.abs(nextDistinct.value - winningValue) : null,
  };
}

export function buildWinnerCounts<TMetricKey extends string>(
  conditions: WinnerCountCondition<TMetricKey>[],
  basis: WinnerCountSummaryBasis,
  metricKeys: TMetricKey[]
): WinnerCountSummary<TMetricKey> {
  const byMetric = {} as WinnerCountSummary<TMetricKey>['byMetric'];
  const overallCounts = new Map<string, WinnerCountRow>();

  metricKeys.forEach((metricKey) => {
    const metricCounts = new Map<string, WinnerCountRow>();

    conditions.forEach((condition) => {
      getTopRankedLeaderGroup(condition, metricKey, basis).leaders.forEach((winner) => {
        const existingMetricCount = metricCounts.get(winner.algorithmKey);
        if (existingMetricCount) {
          existingMetricCount.winCount += 1;
        } else {
          metricCounts.set(winner.algorithmKey, {
            algorithmKey: winner.algorithmKey,
            algorithmName: winner.algorithmName,
            winCount: 1,
          });
        }

        const existingOverallCount = overallCounts.get(winner.algorithmKey);
        if (existingOverallCount) {
          existingOverallCount.winCount += 1;
        } else {
          overallCounts.set(winner.algorithmKey, {
            algorithmKey: winner.algorithmKey,
            algorithmName: winner.algorithmName,
            winCount: 1,
          });
        }
      });
    });

    byMetric[metricKey] = Array.from(metricCounts.values()).sort(
      (a, b) => b.winCount - a.winCount || a.algorithmName.localeCompare(b.algorithmName)
    );
  });

  return {
    byMetric,
    overall: Array.from(overallCounts.values()).sort(
      (a, b) => b.winCount - a.winCount || a.algorithmName.localeCompare(b.algorithmName)
    ),
  };
}
